import React, { useEffect, useRef, useState } from "react";
import { useApp } from "../context/AppContext";
import { t } from "../utils/i18n";
import { api } from "../utils/api";
import { HomeIcon } from "./messages/icons";

// Port 1:1 de OLTODO/components/LocationSearch.tsx (mobile) — recherche d'adresse Nominatim pure,
// retourne {label, lat, lng}, sans notion de lieu OLTODO (contrairement à VenueField.tsx qui
// mélange recherche libre et création/liaison de lieu — pas adapté à une "fausse adresse" perso).
// showSavedAddresses (opt-in) : même carnet "Mes adresses" que côté mobile, voir LocationSearch.tsx.
type Result = { label: string; lat: number; lon: number };

export default function AddressSearch({
  value, onChange, placeholder, showSavedAddresses, inputStyle,
}: {
  value: string;
  onChange: (label: string, lat: number | null, lon: number | null) => void;
  placeholder?: string;
  showSavedAddresses?: boolean;
  inputStyle?: React.CSSProperties;
}) {
  const { lang } = useApp();
  const [query, setQuery] = useState(value);
  const [results, setResults] = useState<Result[]>([]);
  const [selected, setSelected] = useState(!!value);
  const [savedAddresses, setSavedAddresses] = useState<{ id: string; label: string; address: string; latitude: number | null; longitude: number | null }[]>([]);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => { setQuery(value); setSelected(!!value); }, [value]);

  useEffect(() => {
    if (!showSavedAddresses) return;
    api.get("/addresses/mine").then(d => setSavedAddresses(d?.addresses || [])).catch(() => {});
  }, [showSavedAddresses]);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (selected || query.trim().length < 3) { setResults([]); return; }
    debounceRef.current = setTimeout(async () => {
      try {
        const res = await fetch(`https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(query)}&format=json&limit=5`);
        const data = await res.json();
        setResults((data || []).map((d: any) => ({ label: d.display_name, lat: parseFloat(d.lat), lon: parseFloat(d.lon) })));
      } catch { setResults([]); }
    }, 300);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [query, selected]);

  return (
    <div style={{ position: "relative" }}>
      <input
        value={query}
        onChange={e => { setQuery(e.target.value); setSelected(false); onChange(e.target.value, null, null); }}
        placeholder={placeholder || "Rechercher une adresse..."}
        style={{ width: "100%", padding: "10px 12px", borderRadius: 10, border: "1.5px solid #E0E0E0", fontSize: 14, outline: "none", boxSizing: "border-box", ...inputStyle }}
      />
      {showSavedAddresses && !selected && query.trim().length === 0 && savedAddresses.length > 0 && (
        <div style={{ position: "absolute", top: "100%", left: 0, right: 0, marginTop: 4, maxHeight: 220, overflowY: "auto", border: "1px solid #E0E0E0", borderRadius: 10, backgroundColor: "white", boxShadow: "0 4px 16px rgba(0,0,0,0.1)", zIndex: 20 }}>
          <div style={{ padding: "8px 12px", fontSize: 11, fontWeight: 600, color: "#999", backgroundColor: "#F8F8F8", borderBottom: "1px solid #F0F0F0" }}>{t(lang, "side_menu_my_addresses")}</div>
          {savedAddresses.map(a => (
            <div key={a.id} onClick={() => { setQuery(a.address); setSelected(true); onChange(a.address, a.latitude, a.longitude); }} style={{ padding: "10px 12px", cursor: "pointer", borderBottom: "1px solid #F5F5F5" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13, fontWeight: 700 }}><HomeIcon size={13} color="#0a0a0a" /> {a.label}</div>
              <div style={{ fontSize: 11, color: "#999", marginTop: 1, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{a.address}</div>
            </div>
          ))}
        </div>
      )}
      {results.length > 0 && (
        <div style={{ position: "absolute", top: "100%", left: 0, right: 0, marginTop: 4, maxHeight: 180, overflowY: "auto", border: "1px solid #E0E0E0", borderRadius: 10, backgroundColor: "white", boxShadow: "0 4px 16px rgba(0,0,0,0.1)", zIndex: 20 }}>
          {results.map((item, i) => (
            <div
              key={`${item.label}-${i}`}
              onClick={() => { setQuery(item.label); setSelected(true); setResults([]); onChange(item.label, item.lat, item.lon); }}
              style={{ padding: "10px 12px", fontSize: 13, cursor: "pointer", borderBottom: i < results.length - 1 ? "1px solid #F5F5F5" : "none", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}
            >
              {item.label}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
